import { z } from "zod";

/**
 * AI 后端回复契约的唯一 TypeScript 实现，对应
 * `apps/shopify/contract/schemas/reply-message.schema.json`。
 * 挂件的 Socket 帧和后台 Inbox 的历史行都解析这一份，改形状先改那个 JSON Schema。
 *
 * 这里的对象一律 `looseObject`：解析后端响应时保留对象的新增字段。
 * 商品卡数组另按接收规则逐卡清洗并保留前六张。
 * 后端加字段是向后兼容的常规动作，默认的 `z.object` 会把它悄悄删掉，
 * 下游谁也不知道数据到过这里。要收窄形状去下游自己的视图模型里收。
 */

/** 转人工原因。非空即终态转人工；AI 失败也走这个字段，不另发错误事件。 */
export const transferReasonSchema = z.enum([
  "ai_transfer",
  "duplicated",
  "duplicated_question",
  "forbidden",
  "system_error",
]);

export type TransferReason = z.infer<typeof transferReasonSchema>;

/**
 * 商品卡只用于展示；价格和币种后端可能给不出，前端留空而不是把整段会话判死。
 * `item_id` 是变体行的 id，`parent_item_id` 指回父商品，`variant_id` 是 Shopify 的变体 id。
 * 三个 id 一路带到店面和后台：中间层要么用得上，要么原样递下去，别在这里裁掉。
 */
export const replyProductCardSchema = z.looseObject({
  item_id: z.string().min(1),
  parent_item_id: z.string().nullish(),
  variant_id: z.string().nullish(),
  title: z.string().min(1),
  url: z.url(),
  image_url: z.url().nullish(),
  image_alt: z.string().nullish(),
  price: z.string().nullish(),
  currency: z.string().nullish(),
});

export type ReplyProductCard = z.infer<typeof replyProductCardSchema>;

/** 一条终态回复最多展示六张商品卡。 */
export const MAX_REPLY_PRODUCT_CARDS = 6;

/** 逐卡丢弃不合法的商品卡，再按后端顺序保留前六张，正文不受影响。 */
export const replyProductCardsSchema = z.array(z.unknown()).transform((cards) =>
  cards
    .flatMap((card) => {
      const parsed = replyProductCardSchema.safeParse(card);
      return parsed.success ? [parsed.data] : [];
    })
    .slice(0, MAX_REPLY_PRODUCT_CARDS),
);

/** 一行「名称：数值」，两边都是买家可见的原文，客户端不翻译、不换算。 */
const replyLabeledValueSchema = z.looseObject({
  label: z.string().min(1),
  value: z.string().min(1),
});

/**
 * 尺码卡（PRD 2.4）：推荐码、对应美码、三围数据和来源；为什么推荐这个码写在正文里。
 * `source` 跟 grounding.label 一样是买家可见的原文。
 */
export const replySizeCardSchema = z.looseObject({
  recommended_size: z.string().min(1),
  /** 不带 `US` 前缀，前缀由挂件的文案加。 */
  us_size: z.string().nullish(),
  measurements: z.array(replyLabeledValueSchema).max(6),
  source: z.string().nullish(),
});

export type ReplySizeCard = z.infer<typeof replySizeCardSchema>;

const replyCompareColumnSchema = z.looseObject({
  title: z.string().min(1),
  url: z.url().nullish(),
});

/** 对比卡（PRD 2.4）：固定两列，每行是同一个字段在两边的值；哪行不同由客户端判断、高亮。 */
export const replyCompareCardSchema = z.looseObject({
  columns: z.tuple([replyCompareColumnSchema, replyCompareColumnSchema]),
  rows: z
    .array(
      z.looseObject({
        label: z.string().min(1),
        values: z.tuple([z.string(), z.string()]),
      }),
    )
    .min(1)
    .max(12),
});

export type ReplyCompareCard = z.infer<typeof replyCompareCardSchema>;

/** 回答依据。`label` 是买家可见的原文，客户端不从来源类型反推文案。 */
export const groundingSchema = z.looseObject({
  label: z.string().trim().min(1),
  source_types: z.array(z.string()).optional(),
  source_ids: z.array(z.string()).optional(),
});

export type Grounding = z.infer<typeof groundingSchema>;

/** 回复状态只保存引用；逐项校验，坏元素不影响正文。 */
export const replyStateSchema = z
  .looseObject({
    type: z.string().regex(/^[a-z][a-z_]{0,31}$/),
    ref: z
      .string()
      .regex(/^[A-Za-z0-9_.:-]{1,256}$/)
      .optional(),
    task_version: z.int().min(1).max(2147483647).optional(),
  })
  .superRefine((state, ctx) => {
    if (state.type === "order_check_suggested") {
      if (state.ref !== undefined || state.task_version !== undefined)
        ctx.addIssue({
          code: "custom",
          message: "Suggested order check cannot carry a task reference",
        });
    } else if (state.ref === undefined || state.task_version === undefined) {
      ctx.addIssue({
        code: "custom",
        message: "Reply state requires a task reference and version",
      });
    }
  });
export type ReplyState = z.infer<typeof replyStateSchema>;
export function parseReplyStates(input: unknown): ReplyState[] {
  if (!Array.isArray(input)) return [];
  return input
    .flatMap((value) => {
      const parsed = replyStateSchema.safeParse(value);
      return parsed.success ? [parsed.data] : [];
    })
    .slice(0, 5);
}
export const replyStatesSchema = z.unknown().transform(parseReplyStates);

/** 覆盖范围不合法时按旧版未提供字段处理，不能因此丢弃整帧。 */
const answeredMessageIdsSchema = z.unknown().transform((input) => {
  const parsed = z.array(z.string().min(1)).min(1).max(50).safeParse(input);
  return parsed.success ? parsed.data : undefined;
});

/**
 * 一条回复。`quick_response` 是「还在处理」的中间态，`assistant` 才是终态。
 * 商品卡、尺码卡、对比卡和依据只跟着终态回复来，且与 `transfer_reason` 互斥（见 JSON Schema 的 allOf）。
 */
export const replyMessageSchema = z
  .looseObject({
    conversation_id: z.string().min(1),
    message_id: z.string().min(1),
    answered_message_ids: answeredMessageIdsSchema.optional(),
    reply_id: z.string().min(1),
    role: z.enum(["assistant", "quick_response"]),
    content: z.string(),
    /** 不转人工时后端不发这个键。收紧成 `.nullable()` 会把每条回复都丢掉。 */
    transfer_reason: transferReasonSchema.nullish(),
    product_cards: replyProductCardsSchema.optional(),
    size_card: replySizeCardSchema.nullish(),
    compare_card: replyCompareCardSchema.nullish(),
    grounding: groundingSchema.nullish(),
    reply_states: replyStatesSchema.optional(),
  })
  .transform((frame) => {
    const ids = frame.answered_message_ids;
    if (ids !== undefined && !ids.includes(frame.message_id)) {
      return { ...frame, answered_message_ids: [...ids, frame.message_id] };
    }
    return frame;
  });

export type ReplyMessage = z.infer<typeof replyMessageSchema>;
